import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox, Loader, TextInput, useCombobox } from '@any-ui-react/core';
import { useUncontrolled } from '@mantine/hooks';
import { z } from 'zod';

import { TagDisplayGroup, TagDisplayGroupProps } from '../../chat-tag-display/tag-display-group';
import { DefaultOperator } from '../../condition-builder';
import { ValueEditorProps, useValueEditor } from '../../condition-builder';
import { useConditionBuilderStore } from '../../condition-builder/store';
import { RuleType } from '../../condition-builder/types';
import { DEFAULT_RULE } from '../../condition-builder/utils';

import { TagGroupOptionList } from './TagGroupOptionList';
import { TagGroupOptionsProps } from './TagGroupOptions';

const tagValueSchema = z.object({
  id: z.string(),
  name: z.string(),
  isInfluencerTag: z.boolean().optional(),
});

const tagValueStringSchema = z.preprocess((val) => {
  if (typeof val === 'string') {
    try {
      return JSON.parse(val);
    } catch {
      return null;
    }
  }
  return val;
}, tagValueSchema);

type TagValue = z.infer<typeof tagValueSchema>;

const parseTagValue = (value: string): TagValue | null => {
  const result = tagValueStringSchema.safeParse(value);
  return result.success ? result.data : null;
};

const ComboboxOptions = ({
  children,
  loading,
}: {
  children: React.ReactNode;
  loading?: boolean;
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-1">
        <Loader size="xs" />
      </div>
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export const GroupTagSelectValueEditor = (props: ValueEditorProps) => {
  const { t } = useTranslation('crm');
  const { baseProps, getErrorMessage } = useValueEditor(props);
  const state = useConditionBuilderStore((state) => state);

  const combobox = useCombobox();
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<TagValue[]>([]);

  const [selected, setSelected] = useUncontrolled<TagValue[]>({
    value: (props.values || DEFAULT_RULE.values)
      .map((value) => parseTagValue(value))
      .filter((v): v is TagValue => v !== null),
    onChange: (newSelected) => {
      const jsonStringValues = newSelected.map((option) =>
        JSON.stringify({
          id: option.id,
          name: option.name,
          isInfluencerTag: option.isInfluencerTag,
        })
      );
      props.onChange?.(jsonStringValues);
    },
    defaultValue: DEFAULT_RULE.values.map((value) => ({
      id: '',
      name: value,
      isInfluencerTag: false,
    })),
  });

  const handleSelect = useCallback(
    (option: TagValue) => {
      const isSelected = selected.some((s) => s.id === option.id);

      if (isSelected) {
        const newSelected = selected.filter((s) => s.id !== option.id);
        const finalSelected =
          newSelected.length === 0
            ? DEFAULT_RULE.values.map((value) => ({ id: '', name: value, isInfluencerTag: false }))
            : newSelected;
        setSelected(finalSelected);
      } else {
        const newSelected = [...selected, option].filter((s) => s.id !== '');
        setSelected(newSelected);
      }
    },
    [selected, setSelected]
  );

  const filteredOptions = useMemo(() => {
    const usedCustomerTagIds = new Set<string>();

    state.condition.rules.forEach((rule: RuleType<string, string, string[]>) => {
      if (rule.field === 'INFLUENCER_CUSTOMER_TAGS' && rule.values) {
        rule.values.forEach((value: string) => {
          if (value) {
            const parsedValue = parseTagValue(value);
            if (parsedValue?.id) {
              usedCustomerTagIds.add(parsedValue.id);
            }
          }
        });
      }
    });

    return options
      .filter((option) => {
        if (selected.some((s) => s.id === option.id)) {
          return true;
        }
        return !usedCustomerTagIds.has(option.id);
      })
      .filter((option) => option.name.toLowerCase().includes(search.toLowerCase()));
  }, [options, search, selected, state.condition.rules]);

  const tagOptionGroups = useMemo(() => {
    const anychatOptions = filteredOptions.filter(({ isInfluencerTag }) => !isInfluencerTag);
    const anytagOptions = filteredOptions.filter(({ isInfluencerTag }) => isInfluencerTag);

    const selectedIds = selected.map((s) => s.id);

    return [
      {
        type: 'anychat' as const,
        options: anychatOptions,
        selected: selectedIds,
        handleSelect,
      },
      {
        type: 'anytag' as const,
        options: anytagOptions,
        selected: selectedIds,
        handleSelect,
      },
    ] satisfies TagGroupOptionsProps<TagValue>[];
  }, [handleSelect, filteredOptions, selected]);

  const selectedTagGroups = useMemo<TagDisplayGroupProps[]>(() => {
    const anychatSelectedTags = selected.filter(
      ({ name, isInfluencerTag }) => !!name && !isInfluencerTag
    );
    const anytagSelectedTags = selected.filter(
      ({ name, isInfluencerTag }) => !!name && isInfluencerTag
    );

    const groups = [];

    if (anychatSelectedTags.length > 0) {
      groups.push({
        type: 'anychat' as const,
        tags: anychatSelectedTags.map(({ id, name }) => ({ id, name })),
        showSyncStatus: false,
        onRemove: (id?: string) => {
          if (id) {
            const newSelected = selected.filter((s) => s.id !== id);
            setSelected(newSelected);
          }
        },
      });
    }

    if (anytagSelectedTags.length > 0) {
      groups.push({
        type: 'anytag' as const,
        tags: anytagSelectedTags.map(({ id, name }) => ({ id, name })),
        showSyncStatus: false,
        onRemove: (id?: string) => {
          if (id) {
            const newSelected = selected.filter((s) => s.id !== id);
            setSelected(newSelected);
          }
        },
      });
    }

    return groups;
  }, [selected, setSelected]);

  useEffect(() => {
    setLoading(true);

    const loadOptions = async () => {
      if (props.field?.onLoadOptions) {
        const loadOptionsFn = props.field.onLoadOptions();
        const options =
          (await loadOptionsFn()).map(({ id, value, isInfluencerTag }) => ({
            id,
            name: value,
            isInfluencerTag,
          })) || [];

        setOptions(options);
      }
      setLoading(false);
    };
    loadOptions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const error = getErrorMessage();

  // Check for NULL and NOT_NULL operators - return null for these cases
  if (
    ([DefaultOperator.enum.NULL, DefaultOperator.enum.NOT_NULL] as string[]).includes(
      props.operator
    )
  ) {
    return null;
  }

  return (
    <div className={baseProps.className}>
      <div className="flex flex-col">
        <Combobox store={combobox}>
          <Combobox.Target withAriaAttributes={false}>
            <TextInput
              placeholder={t('crm.fields.recipients.tags.placeholder', { ns: 'crm' })}
              value={search}
              rightSection={
                <div className="h-[18px] w-[18px] pr-[3px]">
                  <Combobox.Chevron className="text-gray-7" />
                </div>
              }
              classNames={{ input: 'w-full', section: 'mr-px' }}
              onFocus={() => combobox.toggleDropdown()}
              onBlur={() => {
                setSearch('');
                combobox.closeDropdown();
              }}
              onChange={(e) => setSearch(e.target.value)}
              error={error}
              disabled={props.disabled}
            />
          </Combobox.Target>

          <Combobox.Dropdown className="min-h-max">
            <ComboboxOptions loading={loading}>
              <TagGroupOptionList groups={tagOptionGroups} />
            </ComboboxOptions>
          </Combobox.Dropdown>
        </Combobox>

        <div className="mt-3">
          <TagDisplayGroup groups={selectedTagGroups} disabled={props.disabled} />
        </div>
      </div>
    </div>
  );
};
